import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../store/app-store.js';
import { toLocalDate, formatInTz, type FormatInTzOptions } from '../date/localize.js';
import { formatRelative, formatRelativeShort } from '../date/relative.js';
import type { DateLike } from '../date/parse.js';

export function useTimezone(): { timezone: string; setTimezone: (tz: string) => void } {
  const timezone = useAppStore((s) => s.timezone);
  const setTimezone = useAppStore((s) => s.setTimezone);
  return { timezone, setTimezone };
}

export function useLocale(): { locale: string; setLocale: (l: string) => void } {
  const locale = useAppStore((s) => s.locale);
  const setLocale = useAppStore((s) => s.setLocale);
  return { locale, setLocale };
}

export interface UseLocalTimeResult {
  local: Date;
  formatted: string;
  tz: string;
}

export function useLocalTime(utcInput: DateLike, opts: FormatInTzOptions = {}): UseLocalTimeResult {
  const tz = useAppStore((s) => s.timezone);
  const locale = useAppStore((s) => s.locale);
  return useMemo(() => {
    const local = toLocalDate(utcInput, tz);
    const formatted = formatInTz(utcInput, { timeZone: tz, locale, ...opts });
    return { local, formatted, tz };
  }, [utcInput, tz, locale, JSON.stringify(opts)]);
}

export function useFormattedDate(utcInput: DateLike, opts: FormatInTzOptions = {}): string {
  const tz = useAppStore((s) => s.timezone);
  const locale = useAppStore((s) => s.locale);
  return useMemo(() => formatInTz(utcInput, { timeZone: tz, locale, ...opts }), [utcInput, tz, locale, JSON.stringify(opts)]);
}

export function useRelativeTime(utcInput: DateLike, opts: { short?: boolean; tickMs?: number } = {}): string {
  const { short = false, tickMs = 60_000 } = opts;
  const locale = useAppStore((s) => s.locale);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), tickMs);
    return () => clearInterval(id);
  }, [tickMs]);
  return useMemo(
    () => (short ? formatRelativeShort(utcInput) : formatRelative(utcInput, undefined, locale)),
    [utcInput, locale, short, tick]
  );
}
